import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input } from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { authServices } from "~/services/auth";

type FieldType = {
  password: string;
  confirmPassword: string;
};

export const ResetPasswordView = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const api = useNotification();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ password }: { password: string }) =>
      authServices.resetPassword(token!, password),
    onSuccess: () => {
      api.success({
        message: "Password Reset",
        description: "Your password has been successfully reset.",
      });
      navigate("/login");
    },
    onError: (error: any) => {
      api.error({
        message: "Reset Failed",
        description:
          error.response?.data?.message || "Invalid or expired token",
      });
    },
  });

  const onFinish = (values: FieldType) => {
    if (values.password !== values.confirmPassword) {
      api.error({
        message: "Validation Error",
        description: "Passwords do not match.",
      });
      return;
    }
    if (!token) {
      api.error({
        message: "Invalid Request",
        description: "No reset token provided.",
      });
      return;
    }
    mutate({ password: values.password });
  };

  return (
    <main className="flex-1 flex items-center justify-center px-4 bg-white">
      <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
        {/* Left — Reset Card */}
        <div className="w-full max-w-md flex-shrink-0">
          {/* Brand Name */}
          <div className="mb-8 flex items-center gap-3">
            <h1
              className="text-5xl sm:text-6xl text-[#1a1a2e]"
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}
            >
              Art Handler
            </h1>
            <span
              className="text-lg sm:text-xl text-[#1a1a2e]"
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 400,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                alignSelf: 'flex-end',
                paddingBottom: '0.3em'
              }}
            >
              App
            </span>
          </div>

          <div className="border border-[#d9d9d9] bg-[#f5f5f5] rounded p-6 sm:p-8">
            <h2
              className="text-xs font-bold uppercase tracking-widest text-[#999] text-center mb-8"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Set New Password
            </h2>

            <Form
              layout="vertical"
              onFinish={onFinish}
              className="space-y-6"
              requiredMark={CustomizeRequiredMark}
            >
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please input your new password!" },
                  { min: 8, message: "Password must be at least 8 characters!" },
                ]}
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    New Password
                  </span>
                }
                className="mb-6"
              >
                <Input.Password
                  size="large"
                  id="password"
                  placeholder="Enter new password"
                  className="border border-[#e5e5e5] rounded px-3 py-2 font-bold text-sm uppercase bg-white text-[#1a1a2e] placeholder:text-[#999] focus:border-[#1a1a2e] focus:ring-0 [&_input]:font-bold [&_input]:text-sm [&_input]:bg-white [&_input]:text-[#1a1a2e] [&_input]:placeholder:text-[#999] [&_input]:normal-case"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  disabled={isPending}
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                rules={[
                  { required: true, message: "Please confirm your password!" },
                ]}
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Confirm Password
                  </span>
                }
                className="mb-6"
              >
                <Input.Password
                  size="large"
                  id="confirmPassword"
                  placeholder="Confirm new password"
                  className="border border-[#e5e5e5] rounded px-3 py-2 font-bold text-sm uppercase bg-white text-[#1a1a2e] placeholder:text-[#999] focus:border-[#1a1a2e] focus:ring-0 [&_input]:font-bold [&_input]:text-sm [&_input]:bg-white [&_input]:text-[#1a1a2e] [&_input]:placeholder:text-[#999] [&_input]:normal-case"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  disabled={isPending}
                />
              </Form.Item>

              <Button
                loading={isPending}
                htmlType="submit"
                size="large"
                className="w-full border border-[#1a1a2e] rounded bg-[#1a1a2e] text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {isPending ? "Resetting..." : "Reset Password"}
              </Button>

              <div className="text-center pt-6 border-t border-[#e5e5e5] mt-6">
                <Link
                  to="/login"
                  className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] hover:opacity-70 transition-opacity border-b border-transparent hover:border-[#1a1a2e]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Back to Sign In
                </Link>
              </div>
            </Form>
          </div>
        </div>

        {/* Right — Feature Titles */}
        <div className="hidden lg:flex flex-col flex-1 select-none">
          {[
            "On Demand Art Logistics",
            "Inventory Management",
            "Transportation + Install",
            "Crating + Packing",
            "Worldwide Network",
          ].map((text, i) => (
            <span
              key={i}
              className="block text-[2.8rem] xl:text-[3.5rem] font-black uppercase leading-[1.15]"
              style={{
                fontFamily: "'Oswald', sans-serif",
                color: 'rgba(26, 26, 46, 0.07)',
                letterSpacing: '0.04em',
              }}
            >
              {text}
            </span>
          ))}
        </div>
      </div>
    </main>
  );
};
